import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import {
  Alert,
  Group,
  Loader,
  Select,
  Text,
  Pagination,
  Flex,
  Stack,
} from "@mantine/core";
import {
  getCoreRowModel,
  useReactTable,
  getFilteredRowModel,
  getPaginationRowModel,
} from "@tanstack/react-table";

import { BasicGrid, EmptyMessage } from "@components/shared";

export default function PaginatedList({
  tableData = [],
  displayFormatter,
  responseMapping,
  requestInfo,
}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pageCount, setPageCount] = useState(-1);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState(null);
  const [init, setInit] = useState(false);

  const CancelToken = axios.CancelToken;
  const cancelRef = useRef(null);
  const hasSeedData = Array.isArray(tableData) && tableData.length > 0;

  const dynamicConfig = {
    manualPagination: !hasSeedData,
  };

  if (!hasSeedData) {
    dynamicConfig.pageCount = pageCount > 0 ? pageCount : -1;
  }

  const table = useReactTable({
    ...dynamicConfig,
    data: items,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: {
      pagination: {
        pageSize: 25,
      },
    },
  });

  useEffect(() => {
    if (hasSeedData) {
      setItems(tableData);
      setTotal(tableData.length);
      setLoading(false);
    }
  }, [JSON.stringify(tableData)]);

  useEffect(() => {
    fetchData();
  }, [JSON.stringify(requestInfo)]);

  function fetchData() {
    if (hasSeedData) return;

    setError(null);

    const req = {
      ...requestInfo.data,
      page_size: table.getState().pagination.pageSize,
      page:
        table.getState().pagination.pageIndex === -1
          ? 0
          : table.getState().pagination.pageIndex,
    };

    const cancel = cancelRef.current;
    if (cancel) cancel();

    setLoading(true);

    axios
      .post(requestInfo.url, req, {
        cancelToken: new CancelToken(function executor(c) {
          cancelRef.current = c;
        }),
      })
      .then(({ data }) => {
        const payload = data.response?.[0] || {};
        const rows = payload.data || [];
        setItems(responseMapping ? responseMapping(rows) : rows);
        setPageCount(payload.page_count || 0);
        if (payload.total !== undefined) {
          setTotal(payload.total);
        } else {
          setTotal(rows.length);
        }
      })
      .then(() => {
        setInit(true);
        setLoading(false);
        setError(null);
      })
      .catch((err) => {
        setLoading(false);
        setItems([]);
        setTotal(0);
        setError(typeof err === "string" ? err : "Unable to load list");
        setInit(true);
      });
  }

  useEffect(() => {
    fetchData();
  }, [JSON.stringify(table.getState().pagination)]);

  return (
    <div style={{ marginTop: "0.5em" }}>
      {!loading && total === 0 && !error && <EmptyMessage />}
      {error && !loading && init && (
        <Alert variant="light" color="yellow" title="Oops!">
          {error}
        </Alert>
      )}
      <Stack gap="sm">
        {table.getRowModel().rows.map((item) => displayFormatter(item))}
      </Stack>
      <Group mt="lg" align="center">
        <Pagination
          total={pageCount}
          onChange={(e) => table.setPageIndex(e - 1)}
          size="lg"
          disabled={loading}
        />
        <Text size="sm">
          {total} total item{total === 1 ? "" : "s"}
        </Text>
        {loading && <Loader size="xs" variant="dots" />}
      </Group>
    </div>
  );
}

PaginatedList.defaultProps = {
  tableData: null,
  displayFormatter: (d) => <div>data</div>,
  responseMapping: null,
  requestInfo: {
    url: "",
    data: {},
  },
};
